import { z } from 'zod';
/**
 * Zod contracts for structured AI outputs.
 *
 * These are the HARD gate validated by the AI runtime's SchemaRegistry - distinct
 * from the loose JSON Schemas handed to the provider as a generation hint (the
 * provider's function-calling layer only enforces a subset; see the dual-definition
 * note in packages/ai). Keep these PERMISSIVE enough to preserve each call site's
 * current accept-set, and AVOID `.refine()`/cross-field rules: the runtime feeds
 * `formatZodIssues` output back to the model to self-correct, and refinement
 * messages are not model-actionable. Express tight constraints in post-processing.
 */
// ── Attribute extraction (submit_extractions finalTool) ──────────────────────────
// Mirrors SUBMIT_EXTRACTIONS_SCHEMA in packages/ai base-extraction-skill.ts: only
// `fieldKey` is required per item; `extractions` is required.
export const extractionSourceSchema = z.object({
    documentId: z.number().int().optional(),
    excerpt: z.string().optional(),
});
export const extractionItemSchema = z.object({
    fieldKey: z.string(),
    value: z.string().optional(),
    confidence: z.enum(['high', 'medium', 'low']).optional(),
    sources: z.array(extractionSourceSchema).optional(),
});
export const extractionSubmitSchema = z.object({
    extractions: z.array(extractionItemSchema),
});
// ── Live-meeting deal extraction (single-shot ai.chat array output) ───────────────
// Permissive on the per-item payload (the downstream filter/normalize in
// live-meeting-deal-extract.ts validates fieldKey against known keys and coerces
// currency answers). `answer` is left as unknown because the model may return a
// currency object that is normalized after parse.
export const liveDealExtractItemSchema = z.object({
    type: z.enum(['question_suggestion', 'answer_extracted', 'answer_unknown', 'none']),
    fieldKey: z.string().optional(),
    answer: z.unknown().optional(),
    suggestedQuestion: z.string().optional(),
    /** The prospect's words the answer (or the "don't know") came from. Live API only. */
    evidence: z.string().nullish(),
});
export const liveDealExtractSchema = z.array(liveDealExtractItemSchema);
// ── Copilot intent classification ────────────────────────────────────────────────
// Canonical home for the schema previously inlined in agents/copilot-agent.ts.
export const copilotClassificationSchema = z.object({
    route: z.string(),
    intent: z.string().default('conversation'),
    confidence: z.number().min(0).max(1),
    parameters: z.record(z.string(), z.unknown()).default({}),
    reasoning: z.string().optional(),
});
// ── Repo-copilot skill classification ────────────────────────────────────────────
// Canonical home for the schema previously inlined in agents/repo-copilot.ts.
export const repoClassificationSchema = z.object({
    selectedSkill: z.string(),
    intent: z.string(),
    confidence: z.number().min(0).max(1),
    parameters: z.record(z.string(), z.unknown()).default({}),
    reasoning: z.string().optional(),
    additionalSkills: z
        .array(z.object({
        skillKey: z.string(),
        parameters: z.record(z.string(), z.unknown()).default({}),
    }))
        .optional(),
});
// ── LLM-as-judge verdict ─────────────────────────────────────────────────────────
// Structured output of the response-judge: rubric scores [0,1] + an actionable
// verdict. Flat object with explicit enums (no refinements) so judge errors fed back
// for self-correction are model-actionable.
export const judgeVerdictSchema = z.object({
    /** Is the answer factually/logically correct given the question + evidence? [0,1] */
    correctness: z.number().min(0).max(1),
    /** Are the claims supported by the cited evidence (not hallucinated)? [0,1] */
    groundedness: z.number().min(0).max(1),
    /** Does the answer fully address the question? [0,1] */
    completeness: z.number().min(0).max(1),
    /** Aggregate quality [0,1]; gated against judge_pass_threshold. */
    overall: z.number().min(0).max(1),
    /** Actionable disposition. */
    verdict: z.enum(['pass', 'revise', 'fail']),
    /** Concrete problems found (empty when verdict='pass'). */
    issues: z.array(z.string()).default([]),
    /** Optional improved answer the producer can adopt when verdict='revise'. */
    suggestedRevision: z.string().optional(),
});
// ── Document review (AI pass over a deterministic DocumentProfile) ─────────────────
// A reusable review of one uploaded document: which standard SOW components each
// section maps to, an assessment of the document's logical flow, a completeness
// score, and how good a base it would make for a template. Flat + enum-driven, no
// refinements, so validation errors fed back for self-correction are actionable.
/** The standard parts a Statement of Work document is composed of. */
export const SOW_COMPONENT_KEYS = [
    'cover',
    'executive_summary',
    'client_background',
    'scope',
    'deliverables',
    'assumptions',
    'out_of_scope',
    'timeline_milestones',
    'pricing',
    'payment_schedule',
    'team_roles',
    'terms_conditions',
    'change_management',
    'signature',
    'appendix',
    'other',
];
export const documentReviewSchema = z.object({
    /** Echoed back for alignment with the profile that was reviewed. */
    fileName: z.string(),
    /** Per-section mapping onto a standard SOW component. `slug` matches a sectionFlow slug. */
    components: z
        .array(z.object({
        slug: z.string(),
        heading: z.string(),
        component: z.enum(SOW_COMPONENT_KEYS),
        confidence: z.enum(['high', 'medium', 'low']),
    }))
        .default([]),
    /** The document's narrative arc, ordering issues, and an overall flow score [0,1]. */
    logicalFlow: z.object({
        arc: z.string().default(''),
        issues: z.array(z.string()).default([]),
        score: z.number().min(0).max(1).default(0.5),
    }),
    /** Which components are present / missing, plus a quality score [0,1] and notes. */
    completeness: z.object({
        presentComponents: z.array(z.string()).default([]),
        missingComponents: z.array(z.string()).default([]),
        qualityScore: z.number().min(0).max(1).default(0.5),
        notes: z.array(z.string()).default([]),
    }),
    /** How suitable this document is as the canonical template base [0,1] + reasons. */
    canonicalRecommendation: z.object({
        suitabilityScore: z.number().min(0).max(1).default(0.5),
        reasons: z.array(z.string()).default([]),
    }),
});
/**
 * Stable string keys under which the AI runtime registers these schemas. Kept here
 * (in the shared contract package) so both the schema author and the runtime
 * registry reference one source of truth. The model can also pass these keys to the
 * `validate_schema` tool.
 */
export const AI_OUTPUT_SCHEMA_KEYS = {
    extractionSubmit: 'extraction_submit',
    liveDealExtract: 'deal_extract_result',
    copilotClassification: 'copilot_classification',
    repoClassification: 'repo_classification',
    judgeVerdict: 'judge_verdict',
    documentReview: 'document_review',
    referenceEstimateSet: 'reference_estimate_set',
};
//# sourceMappingURL=ai-output.js.map